import {
  forwardRef,
  Fragment,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";
import cx from "classnames";
/* eslint-disable */
import * as SelectPrimitive from "@radix-ui/react-select";
import {
  Flex,
  Spinner,
  Text,
  IconButton,
  TextField,
  Em,
} from "@radix-ui/themes";
import {
  ChevronDownIcon,
  Cross2Icon,
  MagnifyingGlassIcon,
} from "@radix-ui/react-icons";
import { radixThemeDataAttributes } from "@/components/Theme";
import styles from "./Select.module.scss";

function SelectItemHint({ hint }: { hint?: string }) {
  if (hint == null) {
    return <></>;
  }

  return <Em className={styles.Hint}>{hint}</Em>;
}

export type SelectOption = {
  label: string;
  value: string;
  hint?: string;
  disabled?: boolean;
  icon?: ReactNode;
};

interface SelectItemProps {
  value: string;
  selected?: boolean;
  disabled?: boolean;
  className?: string;
  icon?: ReactNode;
}

const SelectItem = forwardRef(function SelectItem(
  {
    children,
    value,
    selected = false,
    disabled,
    className,
    icon,
  }: PropsWithChildren<SelectItemProps>,
  forwardedRef: React.Ref<HTMLDivElement>
) {
  return (
    <SelectPrimitive.Item
      ref={forwardedRef}
      value={value}
      disabled={disabled}
      className={cx(styles.Item, className, {
        [styles.withIcon]: icon != null,
      })}
      aria-selected={selected}
    >
      {icon}
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
});

interface SearchInputProps {
  value: string;
  onChange: (newValue: string) => void;
}

function SearchInput({ value, onChange }: SearchInputProps) {
  const handleInputKeyDown = useCallback((event: React.KeyboardEvent) => {
    event.stopPropagation();
  }, []);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    },
    [onChange]
  );

  return (
    <Flex width={"100%"} p={"1"}>
      <TextField.Root
        autoFocus
        placeholder="Search…"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleInputKeyDown}
        className={styles.SearchInput}
      >
        <TextField.Slot>
          <MagnifyingGlassIcon />
        </TextField.Slot>
      </TextField.Root>
    </Flex>
  );
}

export interface SelectProps {
  options?: SelectOption[];
  value?: string;
  onChange: (value: string) => void;
  size?: "small" | "medium";
  placeholder?: string;
  classes?: {
    root?: string;
    trigger?: string;
    value?: string;
  };
  startIcon?: JSX.Element;
  loading?: boolean;
  disabled?: boolean;
  allowClear?: boolean;
  allowSearch?: boolean;
  portal?: boolean;
}

function Select({
  options = [],
  value,
  onChange,
  size = "medium",
  placeholder = "Select …",
  classes = {},
  startIcon,
  loading = false,
  disabled = false,
  allowClear = false,
  allowSearch = false,
  portal = true,
}: SelectProps) {
  const [searchTerm, setSearchTerm] = useState<string>("");

  const selectedValue = useMemo(() => {
    return options.find((option) => option.value === value);
  }, [options, value]);

  const showClear = allowClear && selectedValue != null;

  const displayOptions = useMemo(() => {
    if (allowSearch && searchTerm === "") {
      return options;
    }

    return options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, allowSearch, searchTerm]);

  const selectedValueLabel = selectedValue?.label ?? "";
  const selectedValueHint = selectedValue?.hint;

  const Wrapper = portal ? SelectPrimitive.Portal : Fragment;

  return (
    <div
      className={cx(styles.Select, styles[size], classes.root, {
        [styles.disabled]: disabled,
      })}
    >
      <SelectPrimitive.Root
        value={selectedValueLabel}
        onValueChange={onChange}
        disabled={loading || disabled}
      >
        <SelectPrimitive.Trigger
          className={cx(styles.Trigger, classes.trigger)}
        >
          <Flex
            width={"100%"}
            gap={"1"}
            align={"center"}
            className={styles.TriggerContent}
          >
            {(startIcon || loading) && (
              <div className={styles.Icon}>
                {!loading && startIcon}
                {loading && <Spinner />}
              </div>
            )}

            <Flex
              pr={showClear ? "6" : undefined}
              className={cx(styles.Value, classes.value)}
            >
              {selectedValue?.icon}

              <Text truncate>
                <SelectPrimitive.Value
                  placeholder={placeholder}
                  aria-label={selectedValueLabel}
                >
                  {selectedValueLabel}
                </SelectPrimitive.Value>

                <SelectItemHint hint={selectedValueHint} />
              </Text>
            </Flex>

            <SelectPrimitive.Icon className={styles.Icon}>
              <ChevronDownIcon />
            </SelectPrimitive.Icon>
          </Flex>
        </SelectPrimitive.Trigger>

        {showClear && (
          <IconButton
            color="gray"
            variant="soft"
            highContrast
            size="1"
            onClick={(e) => {
              e.stopPropagation();
              onChange("");
            }}
            className={styles.ClearButton}
          >
            <Cross2Icon />
          </IconButton>
        )}

        <Wrapper>
          <SelectPrimitive.Content
            position={"popper"}
            sideOffset={5}
            className={cx(styles.Content, "radix-themes")}
            {...radixThemeDataAttributes}
            style={{ zIndex: 9999 }}
          >
            {allowSearch && (
              <SearchInput value={searchTerm} onChange={setSearchTerm} />
            )}

            <SelectPrimitive.Viewport className={styles.Viewport}>
              {displayOptions.length === 0 ? (
                <SelectItem value={"empty"} disabled>
                  No results found
                </SelectItem>
              ) : (
                <>
                  {displayOptions.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      selected={option.value === value}
                      disabled={option.disabled}
                      icon={option.icon}
                    >
                      {option.label}
                      <SelectItemHint hint={option.hint} />
                    </SelectItem>
                  ))}
                </>
              )}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </Wrapper>
      </SelectPrimitive.Root>
    </div>
  );
}

export default Select;
